import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type Ref } from "react";
import {
  ChevronUp,
  Gauge,
  Maximize2,
  MapPin,
  Minimize2,
  Pause,
  Play,
  Repeat,
  Settings,
  Volume2,
  VolumeX,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  SettingsMenu,
  GuidesOverlay,
  DEFAULT_SETTINGS,
  type SettingsState,
} from "./settings-menu";
import { TimelineScrubber } from "./timeline-scrubber";
import { AnnotationCanvas } from "./annotation-canvas";
import { SavedAnnotationsOverlay } from "./saved-annotations-overlay";
import { AnchorPinOverlay } from "./anchor-pin-overlay";
import { AnchorPinsDisplay } from "./anchor-pins-display";
import { useAnnotation } from "./annotation-context";
import type { WatchComment } from "@/hooks/use-watch";
import { createYouTubeShim, createVimeoShim, type MediaLike } from "./media-shim";

// Playback speed slider — same discrete set Frame.io uses.
// Exported so DualPlayer (compare view) can reuse the exact same control set.
export const SPEED_STEPS = [0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 1.75] as const;
export type Speed = (typeof SPEED_STEPS)[number];

// Source has no fps metadata, so arrow-key frame stepping assumes 30fps: one
// press nudges ~1 frame, Shift = 10. Good enough for review scrubbing.
const FRAME_STEP_FPS = 30;

export type TimecodeFormat = "standard" | "timecode";
export const TIMECODE_FORMAT_KEY = "watch:timecode-format";

type Props = {
  /** Direct playable URL from the CDN. */
  src: string | null | undefined;
  /** External link (migration 0024). When 'youtube'/'vimeo', playback runs
   *  through a shim instead of the native <video>; `src` is ignored. */
  sourceType?: "upload" | "youtube" | "vimeo";
  sourceVideoId?: string | null;
  sourceUrl?: string | null;
  /** Falls back to a static poster area when src is missing (e.g. audio without preview). */
  posterLabel?: string;
  /** Stack-wide comment list. Timeline draws a marker per commented timecode. */
  comments?: WatchComment[];
  /** Watch mode + hook-invalidation key + version target — passed to the anchor-pin floating composer. */
  mode?: "workspace" | "guest" | "share";
  watchKey?: string;
  currentVersionId?: string | null;
  /** Asset type dispatch (2026-07-18). Only video vs audio here — image renders
   *  via ImageViewer, not this component. Audio keeps every playback control
   *  (video element is still what powers audio playback) and adds a decorative
   *  centered SVG icon over the black canvas so the user has something to look
   *  at. Default = "video" for backward compat with existing callers. */
  assetType?: "video" | "audio";
  /** Bidirectional selection with the right panel. Timeline dot click reports
      up via onSelectComment; the current selection lights up the matching marker. */
  selectedCommentId?: string | null;
  onSelectComment?: (id: string) => void;
};

/**
 * Imperative handle so siblings (right panel, composer) can seek + read state
 * without lifting all of the player's local state up. Grown as later tasks
 * need more (e.g. composer will read currentTime to pin new comments).
 */
export type VideoPlayerHandle = {
  seek: (seconds: number) => void;
  getCurrentTime: () => number;
  isPlaying: () => boolean;
  /** Pauses playback if the video is currently playing. No-op otherwise. */
  pause: () => void;
};

/**
 * Custom video player skin (Task 5).
 *
 * Native <video> element with `controls={false}` — we render our own bottom bar
 * to match the Frame.io reference. Layout is 4-cluster:
 *   LEFT   → play/pause / loop / playback-speed / volume
 *   CENTER → timecode display (HH:MM:SS.MS) + Standard/Timecode format toggle
 *   RIGHT  → anchor pin (Task 13) / HD badge / settings gear (Task 6) / fullscreen (Task 7)
 *
 * Right-cluster items other than HD are RENDERED as buttons so the visual
 * spec matches the reference now, but they don't do anything yet — their
 * handlers land in the later tasks noted above. Hover reveal / fullscreen
 * behavior land in Task 7.
 *
 * Timecode format persists to localStorage. HD badge is static — no HLS ladder
 * yet, single-quality source.
 */
// Note: type args are on the ref/props annotations instead of forwardRef's
// generics — Babel's JSX pragma trips on `forwardRef<A, B>(` in .tsx files,
// mistaking `<A,` for the start of a JSX opening tag.
export const VideoPlayer = forwardRef(function VideoPlayer(
  {
    src,
    sourceType = "upload",
    sourceVideoId,
    sourceUrl,
    posterLabel = "No video source",
    comments = [],
    mode = "workspace",
    watchKey,
    currentVersionId = null,
    assetType = "video",
    selectedCommentId = null,
    onSelectComment,
  }: Props,
  ref: Ref<VideoPlayerHandle>,
) {
  const videoRef = useRef<MediaLike | null>(null);
  const surfaceMountRef = useRef<HTMLDivElement>(null);
  const isLink = sourceType === "youtube" || sourceType === "vimeo";
  const rootRef = useRef<HTMLDivElement>(null);

  const [engineTick, setEngineTick] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState<Speed>(1.0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [timecodeFormat, setTimecodeFormat] = useState<TimecodeFormat>(() => {
    if (typeof window === "undefined") return "timecode";
    const stored = window.localStorage.getItem(TIMECODE_FORMAT_KEY);
    return stored === "standard" ? "standard" : "timecode";
  });
  const [settings, setSettings] = useState<SettingsState>(DEFAULT_SETTINGS);
  const annotation = useAnnotation();

  // Fullscreen state (mirrors document.fullscreenElement) + auto-hide logic.
  // In fullscreen we hide controls after 2s of inactivity; any mousemove resets.
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const idleTimerRef = useRef<number | null>(null);

  const kickAutoHide = useCallback(() => {
    setControlsVisible(true);
    if (idleTimerRef.current != null) window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = window.setTimeout(() => setControlsVisible(false), 2000);
  }, []);

  useEffect(() => {
    function onChange() {
      const fs = document.fullscreenElement === rootRef.current;
      setIsFullscreen(fs);
      // When leaving fullscreen, always show controls and clear the timer.
      if (!fs) {
        setControlsVisible(true);
        if (idleTimerRef.current != null) window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      } else {
        // Kick off the auto-hide cycle immediately when entering.
        setControlsVisible(true);
        idleTimerRef.current = window.setTimeout(() => setControlsVisible(false), 2000);
      }
    }
    document.addEventListener("fullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      if (idleTimerRef.current != null) window.clearTimeout(idleTimerRef.current);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!rootRef.current) return;
    if (document.fullscreenElement === rootRef.current) {
      document.exitFullscreen().catch(() => {});
    } else {
      rootRef.current.requestFullscreen().catch(() => {});
    }
  }, []);

  const seekTo = useCallback((seconds: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(seconds, Number.isFinite(v.duration) ? v.duration : seconds));
  }, []);

  // Expose imperative API to the parent (WatchShell). Grow this as needed —
  // right panel uses `seek` on timecode-chip click; composer will use
  // `getCurrentTime` to timestamp new comments.
  useImperativeHandle(
    ref,
    () => ({
      seek: seekTo,
      getCurrentTime: () => videoRef.current?.currentTime ?? 0,
      isPlaying: () => !!videoRef.current && !videoRef.current.paused,
      pause: () => {
        const v = videoRef.current;
        if (v && !v.paused) v.pause();
      },
    }),
    [seekTo],
  );

  // Persist timecode format
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(TIMECODE_FORMAT_KEY, timecodeFormat);
  }, [timecodeFormat]);

  // Sync <video> element state → React
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTime = () => setCurrentTime(v.currentTime);
    const onDurationChange = () => setDuration(Number.isFinite(v.duration) ? v.duration : 0);
    const onVolumeChange = () => {
      setVolume(v.volume);
      setMuted(v.muted);
    };
    const onRateChange = () => setPlaybackRate(v.playbackRate as Speed);

    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("durationchange", onDurationChange);
    v.addEventListener("volumechange", onVolumeChange);
    v.addEventListener("ratechange", onRateChange);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("durationchange", onDurationChange);
      v.removeEventListener("volumechange", onVolumeChange);
      v.removeEventListener("ratechange", onRateChange);
    };
  }, [engineTick]);

  // Native <video> ref callback: stable identity so it fires only on mount/
  // unmount (not every render). Bumps engineTick so the sync effect binds.
  const attachNative = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
    if (el) setEngineTick((t) => t + 1);
  }, []);

  // Link mode: build a YouTube/Vimeo shim into the surface mount, expose it as
  // videoRef.current so every player read/handler works unchanged.
  useEffect(() => {
    if (!isLink) return;
    const mount = surfaceMountRef.current;
    if (!mount) return;
    let disposed = false;
    let shim: MediaLike | null = null;
    // YouTube REPLACES the node it's handed with the iframe; give it a throwaway
    // child so our sized wrapper (mount) survives and the [&>iframe] fill rules
    // apply. Vimeo appends into mount, so it takes mount directly.
    let host: HTMLElement = mount;
    if (sourceType === "youtube") {
      host = document.createElement("div");
      mount.appendChild(host);
    }
    const build =
      sourceType === "youtube"
        ? createYouTubeShim(host, sourceVideoId || "")
        : createVimeoShim(mount, sourceUrl || "");
    build
      .then((s) => {
        if (disposed) { s.destroy?.(); return; }
        shim = s;
        videoRef.current = s;
        setEngineTick((t) => t + 1);
      })
      .catch(() => {});
    return () => {
      disposed = true;
      shim?.destroy?.();
      if (videoRef.current === shim) videoRef.current = null;
      mount.innerHTML = "";
    };
  }, [isLink, sourceType, sourceVideoId, sourceUrl]);

  // Reflect isLooping / playbackRate into the DOM element
  useEffect(() => {
    if (videoRef.current) videoRef.current.loop = isLooping;
  }, [isLooping]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    // If unmuting from 0, restore a sensible volume so user hears something.
    if (!v.muted && v.volume === 0) v.volume = 0.5;
  }, []);

  const onVolumeInput = useCallback((next: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = next;
    v.muted = next === 0;
  }, []);

  // Keyboard shortcuts (2026-07-18):
  //   Space  — toggle play/pause (skipped in inputs so typing a space in the
  //            composer doesn't hijack the player)
  //   T/Y    — zoom Fit / Fill
  //   +/-    — zoom in / out
  //   Ctrl+0 — 100%
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // A Radix menu / dialog that already handled this key (e.g. arrow nav in
      // an open dropdown) calls preventDefault — don't also drive the player.
      if (e.defaultPrevented) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return;
      }
      // Space → play/pause. preventDefault stops the page from scrolling.
      if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        togglePlay();
        return;
      }
      // K → play/pause (NLE standard, mirrors Space)
      if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        togglePlay();
        return;
      }
      // M → mute / unmute
      if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === "m") {
        e.preventDefault();
        toggleMute();
        return;
      }
      // F → fullscreen (plain F only; Ctrl/Cmd+F stays browser find)
      if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === "f") {
        e.preventDefault();
        toggleFullscreen();
        return;
      }
      // ←/→ → frame step (Shift = 10 frames). Pauses first so you land on a frame.
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        const v = videoRef.current;
        if (v) {
          e.preventDefault();
          const frames = e.shiftKey ? 10 : 1;
          const dir = e.key === "ArrowRight" ? 1 : -1;
          if (!v.paused) v.pause();
          seekTo(v.currentTime + (frames / FRAME_STEP_FPS) * dir);
        }
        return;
      }
      // Ctrl+0 → 100%
      if ((e.ctrlKey || e.metaKey) && e.key === "0") {
        e.preventDefault();
        setSettings((s) => ({ ...s, zoomMode: "custom", zoomScale: 1 }));
        return;
      }
      switch (e.key.toLowerCase()) {
        case "t":
          setSettings((s) => ({ ...s, zoomMode: "fit", zoomScale: 1 }));
          break;
        case "y":
          setSettings((s) => ({ ...s, zoomMode: "fill", zoomScale: 1 }));
          break;
        case "+":
        case "=":
          setSettings((s) => ({
            ...s,
            zoomMode: "custom",
            zoomScale: Math.min(4, s.zoomScale * 1.25),
          }));
          break;
        case "-":
          setSettings((s) => ({
            ...s,
            zoomMode: "custom",
            zoomScale: Math.max(0.25, s.zoomScale / 1.25),
          }));
          break;
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [togglePlay, toggleMute, toggleFullscreen, seekTo]);

  // Link mode: a cross-origin YouTube/Vimeo iframe captures keyboard focus the
  // instant it's interacted with, which silently kills our document-level
  // shortcuts (Space/K/M/F/arrows). Pull focus back to the player wrapper
  // whenever the iframe grabs it. The iframe runs controls:0 + disablekb:1, so
  // there is nothing to type into it — reclaiming is safe and doesn't disturb
  // playback. Guarded by document.hasFocus() so an alt-tab away doesn't yank
  // focus back into this tab.
  useEffect(() => {
    if (!isLink) return;
    function reclaim() {
      if (!document.hasFocus()) return;
      const active = document.activeElement;
      if (active && active.tagName === "IFRAME" && rootRef.current?.contains(active)) {
        rootRef.current.focus({ preventScroll: true });
      }
    }
    function onBlur() {
      window.setTimeout(reclaim, 0);
    }
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, [isLink]);

  // Compute the video element's styling from settings.
  const videoStyle = useMemo(() => {
    const baseFit: React.CSSProperties = { maxHeight: "100%", maxWidth: "100%" };
    if (settings.zoomMode === "fill") {
      return { objectFit: "cover" as const, width: "100%", height: "100%" };
    }
    if (settings.zoomMode === "custom") {
      return { ...baseFit, transform: `scale(${settings.zoomScale})`, transformOrigin: "center" };
    }
    return baseFit;
  }, [settings.zoomMode, settings.zoomScale]);

  return (
    <div
      ref={rootRef}
      tabIndex={isLink ? -1 : undefined}
      onMouseMove={isFullscreen ? kickAutoHide : undefined}
      className={cn(
        "relative flex min-h-0 w-full flex-1 flex-col bg-black focus:outline-none",
        isFullscreen && !controlsVisible && "cursor-none",
      )}
    >
      {/* Video canvas */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
        {isLink ? (
          <div
            ref={surfaceMountRef}
            className="relative aspect-video max-h-full w-full max-w-full [&>iframe]:absolute [&>iframe]:inset-0 [&>iframe]:h-full [&>iframe]:w-full"
            style={videoStyle}
          />
        ) : src ? (
          <video
            ref={attachNative}
            src={src}
            preload="metadata"
            playsInline
            onClick={togglePlay}
            style={videoStyle}
          />
        ) : (
          <div className="text-sm text-white/40">{posterLabel}</div>
        )}
        {/* Audio-mode decorative overlay — pure decoration, NOT a real waveform
            (no client-side decode). Frame.io-style vertical bars centered on
            the black rectangle so the user has something to look at while an
            MP3/WAV plays. Pointer-events off so play-on-click still hits the
            <video> below. */}
        {assetType === "audio" && (
          <AudioBarsOverlay playing={isPlaying} />
        )}
        <GuidesOverlay ratio={settings.guidesRatio} showMask={settings.showMask} />
        {/* Annotation canvas — the LIVE DRAW surface. Active while the
            composer is in annotation mode; also stays VISIBLE (pointer-events
            disabled) whenever draft shapes exist, so a user who closes the
            annotation toolbar with Back still sees their drawing on top of
            the video while they type the comment. Cleared on post or on the
            composer's explicit "Clear annotation" affordance. */}
        <AnnotationCanvas visible={annotation.isActive || annotation.shapes.length > 0} />
        {/* Read-only overlay for existing comment annotations. Fetches each
            comment's blob from R2 and renders shapes whose timecode is near
            the current playhead. Controlled by Settings → View on Asset →
            Annotations. */}
        <SavedAnnotationsOverlay
          visible={settings.viewAnnotations && !annotation.isActive}
          comments={comments}
          currentTime={currentTime}
        />
        {/* Anchor-pin overlay — separate from annotation flow. Captures clicks
            when annotation.anchorMode is true, drops a pin + floating composer. */}
        <AnchorPinOverlay
          mode={mode}
          watchKey={watchKey}
          currentVersionId={currentVersionId}
          getCurrentTime={() => videoRef.current?.currentTime ?? 0}
        />
        {/* Existing anchor comments as persistent pins on the canvas — driven
            by Settings → View on Asset → Anchored Comments. Hidden while the
            user is actively dropping a new pin to keep the canvas clean. */}
        <AnchorPinsDisplay
          visible={settings.viewAnchoredComments && !annotation.anchorMode}
          comments={comments}
          onSeek={seekTo}
        />
      </div>

      {/* Timeline scrubber + bottom controls bar. In fullscreen the whole strip
          becomes an absolute overlay that slides in on mouse activity and
          auto-hides after 2s. Theme-aware 2026-07-23: matches Frame.io's
          player-background-control (#fff light / #000 dark). Fullscreen
          overlay stays dark in both themes for max legibility over video. */}
      <div
        className={cn(
          "shrink-0 border-t border-black/5 bg-white text-foreground dark:border-white/5 dark:bg-black dark:text-white",
          isFullscreen &&
            "absolute inset-x-0 bottom-0 z-20 border-t-0 bg-gradient-to-t from-black/90 to-transparent text-white transition-all duration-200",
          isFullscreen && !controlsVisible && "translate-y-full opacity-0",
        )}
        onMouseEnter={isFullscreen ? kickAutoHide : undefined}
      >
        <TimelineScrubber
          currentTime={currentTime}
          duration={duration}
          comments={comments}
          onSeek={seekTo}
          showAnchorMarkers={settings.viewAnchoredComments}
          selectedCommentId={selectedCommentId}
          onSelectComment={onSelectComment}
        />
        <div className="flex items-center gap-2 px-4 pb-2 pt-0">
        {/* LEFT cluster */}
        <div className="flex items-center gap-1">
          <IconButton onClick={togglePlay} label={isPlaying ? "Pause" : "Play"}>
            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </IconButton>
          <IconButton
            onClick={() => setIsLooping((v) => !v)}
            label={isLooping ? "Loop on" : "Loop off"}
            active={isLooping}
          >
            <Repeat className="h-4 w-4" />
          </IconButton>
          <PlaybackSpeedControl value={playbackRate} onChange={setPlaybackRate} />
          <VolumeControl
            volume={muted ? 0 : volume}
            muted={muted}
            onToggleMute={toggleMute}
            onChange={onVolumeInput}
          />
        </div>

        {/* CENTER cluster — timecode */}
        <div className="flex flex-1 items-center justify-center">
          <TimecodeControl
            currentTime={currentTime}
            duration={duration}
            format={timecodeFormat}
            onFormatChange={setTimecodeFormat}
          />
        </div>

        {/* RIGHT cluster — anchor/HD/settings/fullscreen. */}
        <div className="flex items-center gap-1">
          <IconButton
            onClick={() => {
              if (annotation.anchorMode) annotation.deactivateAnchor();
              else annotation.activateAnchor();
            }}
            label="Drop anchor pin"
            active={annotation.anchorMode}
          >
            <MapPin className="h-4 w-4" />
          </IconButton>
          <SettingsMenu value={settings} onChange={setSettings}>
            <Settings className="h-4 w-4" />
          </SettingsMenu>
          <IconButton
            onClick={toggleFullscreen}
            label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </IconButton>
        </div>
      </div>
    </div>
  </div>
  );
});

// ── Sub-components ───────────────────────────────────────────────────────────

export function IconButton({
  children,
  onClick,
  label,
  disabled,
  active,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  label: string;
  disabled?: boolean;
  active?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        "grid h-8 w-8 place-items-center rounded-md text-black/70 transition-colors dark:text-white/80",
        !disabled && "hover:bg-black/10 hover:text-black dark:hover:bg-white/10 dark:hover:text-white",
        disabled && "cursor-not-allowed opacity-40",
        active && "text-primary dark:text-primary",
        className,
      )}
    >
      {children}
    </button>
  );
}

export function PlaybackSpeedControl({
  value,
  onChange,
}: {
  value: Speed;
  onChange: (next: Speed) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => setOpen(false));

  const label = `${value.toFixed(2).replace(/\.?0+$/, "")}x`;
  const idx = SPEED_STEPS.indexOf(value);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Playback speed ${label}`}
        className="flex h-8 w-[4.25rem] items-center justify-center gap-1 rounded-md px-2 text-xs font-medium tabular-nums text-black/70 transition-colors hover:bg-black/10 hover:text-black dark:text-white/80 dark:hover:bg-white/10 dark:hover:text-white"
      >
        <Gauge className="h-3.5 w-3.5 shrink-0" />
        <span className="inline-block w-8 text-center">{label}</span>
      </button>
      {open && (
        <div className="absolute bottom-full left-1/2 z-30 mb-2 w-64 -translate-x-1/2 rounded-lg border border-black/10 bg-white p-4 text-foreground shadow-xl dark:border-white/10 dark:bg-neutral-900 dark:text-white">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-black/60 dark:text-white/60">
            Playback speed
          </div>
          <input
            type="range"
            min={0}
            max={SPEED_STEPS.length - 1}
            step={1}
            value={idx === -1 ? SPEED_STEPS.indexOf(1.0 as Speed) : idx}
            onChange={(e) => onChange(SPEED_STEPS[Number(e.target.value)])}
            className="w-full accent-primary"
          />
          <div className="mt-1 flex justify-between text-[10px] text-black/50 dark:text-white/50">
            {SPEED_STEPS.map((s) => (
              <span key={s}>{s.toString().replace(/^0+/, "")}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function VolumeControl({
  volume,
  muted,
  onToggleMute,
  onChange,
}: {
  volume: number;
  muted: boolean;
  onToggleMute: () => void;
  onChange: (v: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useClickOutside(containerRef, () => setOpen(false));

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={onToggleMute}
        onMouseEnter={() => setOpen(true)}
        aria-label={muted ? "Unmute" : "Mute"}
        className="grid h-8 w-8 place-items-center rounded-md text-black/70 transition-colors hover:bg-black/10 hover:text-black dark:text-white/80 dark:hover:bg-white/10 dark:hover:text-white"
      >
        {muted || volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
      </button>
      {open && (
        <div
          onMouseLeave={() => setOpen(false)}
          className="absolute bottom-full left-1/2 z-30 mb-2 -translate-x-1/2 rounded-lg border border-black/10 bg-white px-3 py-2 shadow-xl dark:border-white/10 dark:bg-neutral-900"
        >
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            onChange={(e) => onChange(Number(e.target.value))}
            aria-label="Volume"
            className="h-1.5 w-28 cursor-pointer accent-primary"
          />
        </div>
      )}
    </div>
  );
}

export function TimecodeControl({
  currentTime,
  duration,
  format,
  onFormatChange,
}: {
  currentTime: number;
  duration: number;
  format: TimecodeFormat;
  onFormatChange: (next: TimecodeFormat) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useClickOutside(containerRef, () => setOpen(false));

  const label = useMemo(() => formatTime(currentTime, format), [currentTime, format]);
  const total = useMemo(() => formatTime(duration, format), [duration, format]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-md bg-black/5 px-3 py-1.5 font-mono text-sm text-foreground transition-colors hover:bg-black/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
        aria-label={`Timecode ${label} of ${total}. Click to change format.`}
      >
        <span>{label}</span>
        <span className="text-black/40 dark:text-white/40">/</span>
        <span className="text-black/60 dark:text-white/60">{total}</span>
        <ChevronUp className={cn("h-3 w-3 text-black/60 transition-transform dark:text-white/60", !open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute bottom-full left-1/2 z-30 mb-2 w-40 -translate-x-1/2 rounded-lg border border-black/10 bg-white py-1 text-foreground shadow-xl dark:border-white/10 dark:bg-neutral-900 dark:text-white">
          <FormatOption
            label="Standard"
            selected={format === "standard"}
            onClick={() => {
              onFormatChange("standard");
              setOpen(false);
            }}
          />
          <FormatOption
            label="Timecode"
            selected={format === "timecode"}
            onClick={() => {
              onFormatChange("timecode");
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

function FormatOption({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-black/10 dark:hover:bg-white/10",
        selected ? "text-foreground dark:text-white" : "text-black/70 dark:text-white/70",
      )}
    >
      {label}
      {selected && <span className="text-primary">✓</span>}
    </button>
  );
}

// ── Time formatting ──────────────────────────────────────────────────────────

/**
 * Format a time (seconds) in one of two formats.
 *   - "standard" → "M:SS"      (e.g. "0:03", "12:47")
 *   - "timecode" → "HH:MM:SS.MS" (e.g. "00:00:03.184") — no framerate math,
 *     millisecond precision comes straight from video.currentTime.
 */
function formatTime(seconds: number, format: TimecodeFormat): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const totalMs = Math.floor(seconds * 1000);
  const hh = Math.floor(totalMs / 3_600_000);
  const mm = Math.floor((totalMs % 3_600_000) / 60_000);
  const ss = Math.floor((totalMs % 60_000) / 1000);
  const ms = totalMs % 1000;

  if (format === "standard") {
    // Show hours only if the video is over an hour long — otherwise M:SS.
    if (hh > 0) return `${hh}:${pad(mm)}:${pad(ss)}`;
    return `${mm}:${pad(ss)}`;
  }
  // timecode: HH:MM:SS.MS with 3-digit ms
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}.${padMs(ms)}`;
}

const pad = (n: number) => n.toString().padStart(2, "0");
const padMs = (n: number) => n.toString().padStart(3, "0");

// ── Audio-mode decorative overlay ────────────────────────────────────────────

/**
 * Static waveform-style bars, centered over the black canvas. Pure decoration.
 * When `playing` is true a subtle scale wobble makes the middle bars breathe
 * — no real audio analysis, just a rAF sine cycle. Cheap.
 */
function AudioBarsOverlay({ playing }: { playing: boolean }) {
  // 25 bars with a static height pattern that clusters around the center.
  // Frozen at build so the visual identity doesn't shift between renders.
  const BARS = useMemo(() => {
    const n = 25;
    return Array.from({ length: n }, (_, i) => {
      // Distance-from-center weight, 0 at ends, 1 in the middle.
      const t = 1 - Math.abs((i - (n - 1) / 2) / ((n - 1) / 2));
      // Deterministic pseudo-random per index so the pattern feels irregular.
      const jitter = 0.35 + ((Math.sin(i * 12.9898) * 43758.5453) % 1 + 1) % 1 * 0.65;
      return Math.max(0.15, 0.25 + t * 0.75 * jitter);
    });
  }, []);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let last = 0;
    const loop = (ts: number) => {
      // Throttle to ~15fps. This waveform is purely decorative, so it doesn't
      // need a state update every animation frame — gating to one update per
      // ~66ms cuts re-renders ~4x with no visible difference. The animation
      // phase still uses the real timestamp, so speed is unchanged.
      if (ts - last >= 66) {
        last = ts;
        setTick(ts / 600);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  return (
    <div className="pointer-events-none absolute inset-0 grid place-items-center">
      <svg
        viewBox="0 0 250 100"
        preserveAspectRatio="xMidYMid meet"
        className="h-24 w-64 text-white/25"
        aria-hidden="true"
      >
        {BARS.map((base, i) => {
          const wobble = playing ? 0.85 + 0.15 * Math.sin(tick + i * 0.5) : 1;
          const h = Math.max(6, base * 80 * wobble);
          const x = 5 + i * 10;
          const y = (100 - h) / 2;
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={4}
              height={h}
              rx={2}
              fill="currentColor"
            />
          );
        })}
      </svg>
    </div>
  );
}

// Fires the callback when the user presses outside `ref`. Used by the
// speed / volume / settings popovers to auto-close. Listens on `pointerdown`
// so a single handler covers mouse, touch, and pen. (The old code registered
// `mousedown` twice — a no-op duplicate — and never handled touch, so the
// popovers didn't reliably dismiss on tap on mobile.)
function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  cb: () => void,
) {
  useEffect(() => {
    function onDown(e: PointerEvent) {
      const el = ref.current;
      if (!el) return;
      if (e.target instanceof Node && !el.contains(e.target)) cb();
    }
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [ref, cb]);
}
